import { AtSign } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { useGuildResources } from '@/hooks/useGuildResources';

interface GuildRoleSelectProps {
  guildId: string | undefined;
  value: string | null;
  onChange: (roleId: string | null) => void;
  allowClear?: boolean;
  /** Offer @everyone as a choice — almost never wanted (an autorole of
   * @everyone is meaningless; a support-role of @everyone defeats the
   * point of a support role), so off by default. */
  includeEveryone?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
}

/**
 * Friendly Discord role picker — same data source, caching, and
 * raw-ID-fallback behavior as GuildChannelSelect (see its docstring);
 * the two are siblings, not variants of one mega-component, since a
 * channel and a role have little in common beyond "Discord id picker".
 * Managed (bot/integration) roles are never offered — bridge/resources.py
 * already excludes them server-side, so there is nothing to filter here.
 */
export function GuildRoleSelect({
  guildId,
  value,
  onChange,
  allowClear = true,
  includeEveryone = false,
  disabled,
  placeholder = 'Select a role…',
  ...aria
}: GuildRoleSelectProps) {
  const { state } = useGuildResources(guildId);

  if (state.kind === 'loading') {
    return <Skeleton className="h-9 w-full" aria-label="Loading roles…" />;
  }

  if (state.kind !== 'ready') {
    return (
      <div className="space-y-1">
        <Input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="Role ID (Discord data unavailable)"
          inputMode="numeric"
          disabled={disabled}
          aria-label={aria['aria-label'] ?? 'Role ID'}
        />
        <p className="text-micro text-text-tertiary">
          {state.kind === 'forbidden'
            ? "Discord data unavailable — you don't have permission to view this server's roles."
            : 'Discord data unavailable — enter the role ID manually.'}
        </p>
      </div>
    );
  }

  const roles = state.data.roles.filter((r) => includeEveryone || !r.isEveryone);
  const found = value ? state.data.roles.find((r) => r.id === value) : undefined;
  const isUnknown = Boolean(value) && !found;

  return (
    <div className="space-y-1">
      <div className="relative">
        <AtSign size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary z-10" aria-hidden="true" />
        <Select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className="pl-9"
          aria-label={aria['aria-label'] ?? 'Role'}
        >
          {allowClear && <option value="">— None —</option>}
          {!allowClear && !value && <option value="" disabled>{placeholder}</option>}
          {isUnknown && <option value={value!}>Unknown role ({value})</option>}
          {roles.map((r) => (
            <option key={r.id} value={r.id}>{r.isEveryone ? '@everyone' : r.name}</option>
          ))}
        </Select>
      </div>
      {isUnknown && (
        <p className="text-micro text-warning">
          This role no longer appears in the server — it may have been deleted.
        </p>
      )}
    </div>
  );
}
